package com.swetdot.swetgram.ui.discover

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.ChatRepository
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.discover.DiscoverRepository
import com.swetdot.swetgram.data.discover.asDiscoverFailure
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.DiscoverComment
import com.swetdot.swetgram.domain.DiscoverCommentId
import com.swetdot.swetgram.domain.DiscoverCommentWrite
import com.swetdot.swetgram.domain.DiscoverContent
import com.swetdot.swetgram.domain.DiscoverFailure
import com.swetdot.swetgram.domain.DiscoverLikeResult
import com.swetdot.swetgram.domain.DiscoverPost
import com.swetdot.swetgram.domain.DiscoverPostStatus
import com.swetdot.swetgram.domain.DiscoverPostId
import com.swetdot.swetgram.domain.DiscoverPostWrite
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.domain.Page
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.ui.chat.dayLabel
import com.swetdot.swetgram.ui.chat.dayLabelText
import com.swetdot.swetgram.ui.chat.formatClockTime
import com.swetdot.swetgram.ui.profile.profileMessage
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.text
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.discover_groups_comment_deleted
import swetgram.shared.generated.resources.discover_groups_answer_action_failed
import swetgram.shared.generated.resources.discover_groups_comment_post_failed
import swetgram.shared.generated.resources.discover_groups_comments_more_failed
import swetgram.shared.generated.resources.discover_groups_delete_comment_failed
import swetgram.shared.generated.resources.discover_groups_delete_post_failed
import swetgram.shared.generated.resources.discover_groups_edit_window_expired
import swetgram.shared.generated.resources.discover_groups_like_comment_failed
import swetgram.shared.generated.resources.discover_groups_like_limit
import swetgram.shared.generated.resources.discover_groups_not_the_author
import swetgram.shared.generated.resources.discover_groups_post_load_failed
import swetgram.shared.generated.resources.discover_groups_plan_action_failed
import swetgram.shared.generated.resources.discover_groups_plan_closed
import swetgram.shared.generated.resources.discover_groups_plan_full
import swetgram.shared.generated.resources.discover_groups_post_unavailable_title
import swetgram.shared.generated.resources.discover_groups_recipient_unavailable
import swetgram.shared.generated.resources.discover_groups_save_failed
import swetgram.shared.generated.resources.discover_groups_bookmark_failed
import swetgram.shared.generated.resources.discover_groups_share_failed
import swetgram.shared.generated.resources.discover_groups_share_sent
import kotlinx.coroutines.flow.first
import swetgram.shared.generated.resources.discover_groups_stale_comment
import swetgram.shared.generated.resources.discover_groups_stale_post
import swetgram.shared.generated.resources.discover_groups_stale_reload
import swetgram.shared.generated.resources.discover_groups_timestamp
import swetgram.shared.generated.resources.discover_groups_unlike_failed
import swetgram.shared.generated.resources.discover_groups_verify_email_to_comment
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * SPS §11.5's post detail screen: "original post; comments; comment composer; author actions;
 * report action".
 *
 * A NOTE ON THE SECTION NUMBERS THIS PASS WAS HANDED. The brief cites "SPS §14-15 Discover" and
 * "SPS §15 for what a post detail holds". Those numbers do not match the reference folder:
 * `swetgram_sps.md` puts Discover at §11 (§11.4 Post Card, §11.5 Post Details, §11.6 Comments,
 * §11.7 Likes, §11.8 Message Author), while §14 is Global Search and §15 is Notifications.
 * Everything below cites the section that actually holds the requirement. `domain/DiscoverPost.kt`
 * records the same correction independently.
 *
 *
 * SPS §11.5 ASKS FOR FIVE THINGS. FOUR ARE BUILT.
 *
 *  * ORIGINAL POST -- `getDiscoverPost`, rendered by `DiscoverPostCard`, the same component the feed
 *    uses. A post the user tapped looks like the post they tapped.
 *  * COMMENTS -- `listDiscoverComments`, oldest first, cursor-paged.
 *  * COMMENT COMPOSER -- `createDiscoverComment`.
 *  * AUTHOR ACTIONS -- `editDiscoverPost`, `deleteDiscoverPost`, `editDiscoverComment`,
 *    `deleteDiscoverComment`, plus the four like operations. This screen is where SPS §11.5's
 *    "author actions" live; `DiscoverPostCard`'s own note explains why the feed card carries none.
 *  * REPORT ACTION -- **NOT BUILT, AND NOT AN OVERSIGHT.** `POST /reports` (`createReport`) is in the
 *    frozen contract and is implemented by nothing: no route file mentions it, `Module.kt` installs
 *    nothing for it, and the deployed server answered **404** to
 *    `POST /reports {"target_type":"discover_post",...}` when this pass probed it on 2026-08-06.
 *    `Navigation.kt` already reached the same conclusion for the public profile -- it passes no
 *    `ReportSubmitter` "rather than offering one that would 404". The specification wants the
 *    control; the server cannot serve it yet.
 *
 * SDS §13's action list adds SHARE, which is likewise NOT DRAWN.
 * `shareDiscoverPostToConversation` is routed nowhere and answers a bodyless 404 -- pinned by a
 * server test in pass 1 and re-confirmed live on 2026-08-06. A share button that cannot share is
 * worse than no share button, because it teaches the user their post went somewhere.
 *
 * WHAT IS BUILT THAT THE FEED CARD DEFERRED: SPS §5.1's "Message author from Discover post" as a
 * control on the post itself. `DiscoverPostCard` reaches it through the public profile, which is one
 * screen away and answers "who is this person" on the way; the detail screen is where somebody has
 * already decided to engage, so the action is direct. Both routes end in the same
 * `createOrGetDirect`.
 *
 *
 * THERE IS NO REALTIME AND NO PUSH BEHIND THIS SCREEN. `outbox.conversation_id` and
 * `push_deliveries.conversation_id` are both NOT NULL and a Discover comment has no conversation, so
 * nothing about this thread ever arrives on its own: a comment on your post notifies nobody, and an
 * open detail screen never learns about one. That is why [PostDetailUiState.lastLoadedAt] exists and
 * why the screen prints `freshnessLabel` from it. A thread that silently never updates is worse than
 * one that says when it was last read. See DECISIONS §8, "No push and no outbox event for a Discover
 * comment or like".
 *
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT ASSERT
 *
 *  * NO PRESENCE. Every author here is the `User` the repository produced from the server's
 *    already-projected `UserPublic`, and no author gets an online dot -- see `CommentRow` for the
 *    argument, which is `GroupPersonRow`'s and `DiscoverPostCard`'s.
 *  * NO MENTIONS. `domain/DiscoverPost.kt` drops the field because the deployed server always sends
 *    `[]`, so no surface here can depend on it.
 *  * NO GUESS ABOUT WHY A POST IS MISSING. The server answers one 404 for "deleted", "never existed",
 *    "in a group you left" and "written by somebody you blocked", deliberately, and the screen says
 *    one sentence.
 *  * NO "EDITED" MARK ON A COMMENT. `wasEdited` is derived from `updated_at != created_at` and the
 *    deployed server sets those a microsecond apart at INSERT time on roughly one row in ten --
 *    measured, 4 of 40 comments, on 2026-08-06. `CommentRow`'s header carries the numbers and names
 *    the one-line fix, which belongs in `DiscoverRepository`'s mapper rather than here.
 */

// ---------------------------------------------------------------------------------------------
// The source
// ---------------------------------------------------------------------------------------------

/**
 * The nine repository calls this screen makes, and nothing else.
 *
 * AN INTERFACE RATHER THAN THE CONCRETE `DiscoverRepository`, for the reason `ProfileViewModel`
 * declares `ReportSubmitter`: it is what makes this view model constructible in a test.
 * `ChatViewModelTest`'s header records what the alternative costs -- it cannot build a
 * `ChatViewModel` at all, because that one "takes a concrete `MessageRepository` and a concrete
 * `RealtimeClient`, not interfaces, and the repository needs a `SwetgramCache`" -- so every decision
 * that screen makes had to be lifted out and tested beside it instead. The risky decisions here are
 * about permission and about what a refusal means, so the seam is drawn where they can be exercised.
 *
 * EVERY SIGNATURE IS `DiscoverRepository`'s, VERBATIM, including the sealed return types. Nothing is
 * flattened on the way through, because each of those types exists to stop a caller assuming: a
 * `409` on an edit is a value rather than an exception so a `when` has to handle it, and a like's
 * four outcomes are four different things to do. [asPostDetailSource] is a one-line adapter, so the
 * repository needs no change to be wired in.
 */
interface DiscoverPostDetailSource {

    /** `getDiscoverPost`. 404 for absent, deleted, invisible and blocked-author alike. */
    suspend fun post(id: DiscoverPostId): DiscoverPost

    /**
     * `listDiscoverComments`. OLDEST FIRST -- the server's ordering, so a page appends to the END of
     * what is already shown. A null [cursor] asks for the first page.
     *
     * This operation declares no `If-None-Match` and never answers 304, unlike the two feeds, which
     * is why it is a plain `Page` rather than the feed's richer type.
     */
    suspend fun comments(postId: DiscoverPostId, cursor: String? = null): Page<DiscoverComment>

    /**
     * `createDiscoverComment`. 201.
     *
     * NOT IDEMPOTENT. The contract's body is `required: [content]` and the operation declares no
     * `Idempotency-Key`, so the server has nothing to deduplicate against and a retried create makes
     * a SECOND comment. Nothing above this line may retry it automatically -- see [sendComment].
     */
    suspend fun addComment(postId: DiscoverPostId, content: String): DiscoverComment

    /** `editDiscoverComment`. `Stale` carries nothing: there is no `getDiscoverComment` in v1.0.0. */
    suspend fun editComment(id: DiscoverCommentId, content: String, version: Int): DiscoverCommentWrite

    /**
     * `deleteDiscoverComment`. 204 ALWAYS -- for a comment that never existed, one already gone, and
     * one belonging to somebody else alike, because the row is addressed `(id, author_id)`.
     *
     * That is the whole reason [CommentRowActions.canDelete] is never true for a comment the caller
     * did not write: the screen would otherwise report success over a comment that is still there.
     */
    suspend fun deleteComment(id: DiscoverCommentId)

    /** `editDiscoverPost`. `Stale` carries the server's current post, already re-read. */
    suspend fun editPost(id: DiscoverPostId, content: String, version: Int): DiscoverPostWrite

    /**
     * `deleteDiscoverPost`. Author only, NO window -- DECISIONS §8, "Authors may delete their posts
     * and comments at any time". Permanent, and it takes the whole thread with it by cascade.
     */
    suspend fun deletePost(id: DiscoverPostId)

    /** The STATE to reach, never a flip. `DiscoverRepository.setPostLiked` explains why. */
    suspend fun setPostLiked(id: DiscoverPostId, liked: Boolean): DiscoverLikeResult

    /** Absolute, server-synchronised bookmark state. */
    suspend fun setPostSaved(id: DiscoverPostId, saved: Boolean): DiscoverPost

    suspend fun sharePost(id: DiscoverPostId, chatId: ChatId)

    /** [setPostLiked]'s twin, one table down, and always `Accepted` rather than `Settled`. */
    suspend fun setCommentLiked(id: DiscoverCommentId, liked: Boolean): DiscoverLikeResult

    suspend fun joinPlan(id: DiscoverPostId): DiscoverPost
    suspend fun leavePlan(id: DiscoverPostId): DiscoverPost
    suspend fun updatePlanStatus(id: DiscoverPostId, status: DiscoverPostStatus, version: Int): DiscoverPost
    suspend fun acceptAnswer(postId: DiscoverPostId, commentId: DiscoverCommentId, version: Int): DiscoverPost
    suspend fun clearAcceptedAnswer(postId: DiscoverPostId, version: Int): DiscoverPost
}

/**
 * The repository, as this screen's source.
 *
 * A one-line adapter rather than a supertype on `DiscoverRepository`, so wiring this screen needs no
 * edit to a file another pass owns. If the repository ever declares `: DiscoverPostDetailSource`
 * directly, this can go.
 */
fun DiscoverRepository.asPostDetailSource(): DiscoverPostDetailSource =
    object : DiscoverPostDetailSource {
        override suspend fun post(id: DiscoverPostId) = this@asPostDetailSource.post(id)
        override suspend fun comments(postId: DiscoverPostId, cursor: String?) =
            this@asPostDetailSource.comments(postId, cursor)
        override suspend fun addComment(postId: DiscoverPostId, content: String) =
            this@asPostDetailSource.addComment(postId, content)
        override suspend fun editComment(id: DiscoverCommentId, content: String, version: Int) =
            this@asPostDetailSource.editComment(id, content, version)
        override suspend fun deleteComment(id: DiscoverCommentId) =
            this@asPostDetailSource.deleteComment(id)
        override suspend fun editPost(id: DiscoverPostId, content: String, version: Int) =
            this@asPostDetailSource.editPost(id, content, version)
        override suspend fun deletePost(id: DiscoverPostId) = this@asPostDetailSource.deletePost(id)
        override suspend fun setPostLiked(id: DiscoverPostId, liked: Boolean) =
            this@asPostDetailSource.setPostLiked(id, liked)
        override suspend fun setPostSaved(id: DiscoverPostId, saved: Boolean) =
            this@asPostDetailSource.setPostSaved(id, saved)
        override suspend fun sharePost(id: DiscoverPostId, chatId: ChatId) =
            this@asPostDetailSource.sharePost(id, chatId)
        override suspend fun setCommentLiked(id: DiscoverCommentId, liked: Boolean) =
            this@asPostDetailSource.setCommentLiked(id, liked)
        override suspend fun joinPlan(id: DiscoverPostId) = this@asPostDetailSource.joinPlan(id)
        override suspend fun leavePlan(id: DiscoverPostId) = this@asPostDetailSource.leavePlan(id)
        override suspend fun updatePlanStatus(id: DiscoverPostId, status: DiscoverPostStatus, version: Int) =
            this@asPostDetailSource.updatePlanStatus(id, status, version)
        override suspend fun acceptAnswer(postId: DiscoverPostId, commentId: DiscoverCommentId, version: Int) =
            this@asPostDetailSource.acceptAnswer(postId, commentId, version)
        override suspend fun clearAcceptedAnswer(postId: DiscoverPostId, version: Int) =
            this@asPostDetailSource.clearAcceptedAnswer(postId, version)
    }

// ---------------------------------------------------------------------------------------------
// The rules -- pure, so they can be exercised without a repository and without a renderer
// ---------------------------------------------------------------------------------------------

/**
 * What the post offers this viewer.
 *
 * EVERY FLAG IS THE DOMAIN'S ANSWER, NOT A SECOND READING OF IT. `DiscoverPost.mayEdit` and
 * `mayDelete` already encode the 48-hour window, the author-only rule and the "no date, no edit"
 * fallback; re-deriving any of that here is how two surfaces start disagreeing about who owns a post.
 * This type exists only so a composable does not have to hold the signed-in user's id and a clock.
 */
data class PostDetailActions(
    val canEdit: Boolean,
    val canDelete: Boolean,
    /**
     * SPS §5.1's "Message author from Discover post" and §11.8's flow -- offered for anybody but
     * yourself.
     *
     * NOT GATED ON ANYTHING ELSE, deliberately. The client cannot know in advance whether the author
     * accepts messages: the server answers a block in either direction and a restrictive
     * `allow_messages_from` with the SAME neutral `403 recipient_unavailable`, so that no client can
     * turn the refusal into a block detector. Hiding the button on a guess would be that detector,
     * rebuilt in the client.
     */
    val canMessageAuthor: Boolean,
    /**
     * SPS §11.5's and SDS §13's Report -- offered for anybody's post but your own.
     *
     * NOT the same question as [canDelete] and deliberately its complement: the author of a post
     * removes it, and everybody else reports it. The server would accept a report against your own
     * post (`createReport` puts no ownership condition on a `discover_post` target), so this is a
     * product decision rather than the contract's, and it is the same one SDS §12.5 states outright
     * for messages: "Report should be available for messages not sent by the current user."
     *
     * It says nothing about whether reporting is WIRED -- that is the screen's `onReport`, which is
     * null on a build with no report repository behind it.
     */
    val canReport: Boolean,
) {
    companion object {
        /** Nothing is offered before a post has loaded. */
        val NONE = PostDetailActions(
            canEdit = false,
            canDelete = false,
            canMessageAuthor = false,
            canReport = false,
        )

        fun of(post: DiscoverPost, me: UserId, now: Instant) = PostDetailActions(
            canEdit = post.mayEdit(me, now),
            canDelete = post.mayDelete(me),
            canMessageAuthor = !post.isMine(me),
            canReport = !post.isMine(me),
        )
    }
}

/**
 * What one comment offers this viewer.
 *
 * LIKING IS ALWAYS OFFERED and so has no flag here -- including on your own comment, and including
 * across a block. That is the server's decision and it is deliberate: `DiscoverSocialService` gates
 * comment CREATION on blocks and explicitly does not gate likes, because "a blocked person's like
 * reaches the author as `+1` and nothing else" and "a 403 on a like, where nothing was going to be
 * delivered, is a pure oracle".
 */
data class CommentRowActions(
    val canEdit: Boolean,
    /**
     * The comment's author, and nobody else -- NOT the post's author, and not a group admin.
     *
     * This is the one control on the screen where drawing it wrongly produces a silent lie rather
     * than an error. `deleteDiscoverComment` addresses the row `(id, author_id)`, so deleting
     * somebody else's comment answers **204** and removes nothing; a screen that offered it would
     * report success and the comment would still be there on the next refresh. Confirmed live on
     * 2026-08-06 -- a second `DELETE` of an already-deleted comment answered 204 as well.
     *
     * `DiscoverSocialService` records the same reading from the other side: "SPS 11.5's 'author
     * actions' on the post-detail screen is not read as a moderation power here". The sweeping remedy
     * a post's author does hold is deleting the post, which cascades.
     */
    val canDelete: Boolean,
    /**
     * SPS §16.3 names `discover_comment` among the five reportable things -- offered for anybody's
     * comment but your own, exactly as [PostDetailActions.canReport] is for a post.
     */
    val canReport: Boolean,
) {
    companion object {
        val NONE = CommentRowActions(canEdit = false, canDelete = false, canReport = false)

        fun of(comment: DiscoverComment, me: UserId, now: Instant) = CommentRowActions(
            canEdit = comment.mayEdit(me, now),
            canDelete = comment.mayDelete(me),
            canReport = !comment.isMine(me),
        )
    }
}

/**
 * A composer's text, and the one question a composer has to ask about it.
 *
 * TRIMMED BEFORE COUNTING and trimmed before sending, because the server does the same:
 * `DiscoverSocialService.validComment` trims first so that "a comment of nothing but whitespace is a
 * 400 rather than a blank row in a thread". Counting the untrimmed string would let a draft of three
 * spaces light up the Post button.
 *
 * MEASURED BOTH WAYS, AND THE STRICTER ONE WINS. That is not belt-and-braces; the two counts
 * genuinely disagree and each has a caller that enforces it:
 *
 *  * THE CONTRACT AND THE DATABASE COUNT CODE POINTS. JSON Schema's `maxLength` does, and so does
 *    Postgres' `char_length`, which is what `discover_comments_content_len` uses -- so 300 emoji is
 *    300 characters to both, and 600 to `String.length`.
 *  * `DiscoverRepository.addComment` AND `editComment` COUNT UTF-16 UNITS, in a `require` that throws
 *    `IllegalArgumentException` rather than returning a failure. A draft of 300 emoji is inside the
 *    contract's limit and outside that `require`, so sending it would raise an exception from a
 *    layer this screen reports as "could not post your comment".
 *
 * Refusing at the smaller of the two keeps that path unreachable. It costs the emoji-heavy writer
 * some room they are entitled to, which is the harmless direction; the fix belongs in the repository,
 * and is noted there rather than worked around twice.
 */
data class DraftState(
    /** What would actually be sent. */
    val text: String,
    /** Code points remaining against the contract's limit. Negative when over. */
    val remaining: Int,
    val canSend: Boolean,
) {
    /**
     * Whether to print the counter at all.
     *
     * The SDS says nothing about character counters, so this is a DECISION: a counter that is always
     * on turns a composer into a form, and one that appears near the limit is a warning rather than a
     * scold.
     */
    val showCounter: Boolean get() = remaining <= COUNTER_THRESHOLD
}

/** How close to the limit the counter appears. A DECISION; the folder is silent. */
private const val COUNTER_THRESHOLD = 40

/**
 * Code points, for the reason [DraftState] gives. `String.length` counts UTF-16 units, so a string
 * of astral-plane characters -- emoji, and most of them are -- measures double.
 */
internal fun codePointLength(text: String): Int {
    var count = 0
    var index = 0
    while (index < text.length) {
        val isPair = text[index].isHighSurrogate() &&
            index + 1 < text.length &&
            text[index + 1].isLowSurrogate()
        index += if (isPair) 2 else 1
        count++
    }
    return count
}

internal fun measureDraft(raw: String, maxLength: Int): DraftState {
    val text = raw.trim()
    val points = codePointLength(text)
    return DraftState(
        text = text,
        remaining = maxLength - points,
        // Both counts, for the reason on [DraftState]. `text.length` is never smaller than `points`.
        canSend = points in 1..maxLength && text.length <= maxLength,
    )
}

/**
 * Whether a KEYSTROKE may be accepted at all, as opposed to whether the draft may be SENT.
 *
 * The two are different questions and both are needed. [measureDraft] trims before counting, because
 * that is what would be sent and what the server measures; this does not, because the character
 * being typed is at the end of the string and a trailing space the user is in the middle of typing
 * is still occupying room in the field. Refusing on the trimmed length would let somebody type past
 * the limit and then watch the tail vanish on send.
 *
 * BOTH COUNTS, AND THE SMALLER ONE WINS, for the reason [DraftState] records at length: the contract
 * and Postgres count CODE POINTS, `DiscoverRepository.addComment` and `editComment` count UTF-16
 * units in a `require` that THROWS rather than returning a failure, and a draft of 300 emoji is
 * inside the first and outside the second. Refusing at the smaller keeps that exception path
 * unreachable.
 *
 * IT LIVES HERE RATHER THAN IN A SCREEN, and that placement is the whole reason it is written down.
 * It was a private function in `PostDetailScreen.kt`, which meant the comments sheet could not reach
 * it and would have had to declare its own -- two copies of a rule whose entire job is to agree with
 * the repository's `require`. A limit rule that exists twice is a limit rule that will one day be
 * raised once.
 */
internal fun withinDraftLimit(text: String, maxLength: Int): Boolean =
    codePointLength(text) <= maxLength && text.length <= maxLength

/** `DiscoverComment.content`: minLength 1, maxLength 500. */
internal fun commentDraftState(raw: String): DraftState =
    measureDraft(raw, DiscoverContent.COMMENT_MAX)

/** `DiscoverPost.content`: minLength 1, maxLength 1000. */
internal fun postDraftState(raw: String): DraftState =
    measureDraft(raw, DiscoverContent.POST_MAX)

/**
 * The Discover error table, and the ONE place it lives.
 *
 * `DiscoverViewModel.discoverFeedBanner` is a thin shell over this, overriding exactly the two
 * sentences a feed needs to say differently and falling through for everything else -- so there is
 * one table rather than two that drift. Declared here because the DETAIL screen is where most of
 * these refusals are reachable: a lapsed edit window, an edit that is not yours, a comment from an
 * unverified account, and a stale `version` are all things only this surface can provoke.
 *
 * Everything with no Discover meaning falls through to `profileMessage` -- offline, a timeout, an
 * expired session, an unsupported build -- so those are spelled one way across the whole app. A
 * second copy of that table is exactly what `ProfileViewModel.withSaveFailure` says it refuses to
 * make.
 *
 * Branches on `asDiscoverFailure`, the boundary's own mapping, so no UI file compares error codes or
 * imports `protocol` (IMPLEMENTATION_NOTES §10).
 *
 * THE MESSAGE-AUTHOR REFUSAL IS DELIBERATELY NOT IN THIS TABLE. `403 recipient_unavailable` maps to
 * `DiscoverFailure.OTHER`, correctly, because it is not a Discover failure at all -- it comes from
 * `createOrGetDirect`. See [PostDetailViewModel.messageAuthor], which reports it without decoding it.
 *
 * THE 404 REACHES THE EMPTY STATE'S OWN KEY rather than a second spelling of the same sentence.
 * `discover_groups_post_unavailable_title` is what the screen prints when the post is gone; a
 * banner saying the same thing in different words would be two answers to one fact, and the second
 * one would drift the moment either was translated.
 */
internal fun Throwable.discoverBanner(fallback: UiText): UiText = when (asDiscoverFailure()) {
    DiscoverFailure.UNAVAILABLE -> Res.string.discover_groups_post_unavailable_title.text()
    // TERMINAL -- no reload and no permission makes it editable again, so the sentence names the
    // thing that does still work. SDS §18.2: say what happened and what to do next.
    DiscoverFailure.EDIT_WINDOW_EXPIRED -> Res.string.discover_groups_edit_window_expired.text()
    DiscoverFailure.NOT_THE_AUTHOR -> Res.string.discover_groups_not_the_author.text()
    DiscoverFailure.EMAIL_NOT_VERIFIED -> Res.string.discover_groups_verify_email_to_comment.text()
    DiscoverFailure.STALE -> Res.string.discover_groups_stale_reload.text()
    DiscoverFailure.PLAN_FULL -> Res.string.discover_groups_plan_full.text()
    DiscoverFailure.PLAN_CLOSED -> Res.string.discover_groups_plan_closed.text()
    else -> profileMessage(fallback)
}

/**
 * The one-line notice for a COMMENT like that did not land.
 *
 * `DiscoverViewModel.likeNotice` covers the post case and is reused unchanged for it; this exists
 * only because its two named sentences are about a post in a feed. "That post has been deleted."
 * is the wrong noun here, and a comment that has gone leaves a row on THIS screen rather than a card
 * in a list somebody is scrolling.
 *
 * [liked] is which way the tap was going, so the sentence is about what the user tried to do rather
 * than about a verb the app chose.
 */
internal fun commentLikeNotice(failure: DiscoverFailure, liked: Boolean): UiText = when (failure) {
    DiscoverFailure.UNAVAILABLE -> Res.string.discover_groups_comment_deleted.text()
    // DECISIONS §12: 300 a day, and posts and comments share the ONE bucket -- so this sentence has
    // to be true of both, and `likeNotice` reaches the SAME KEY for the same reason.
    DiscoverFailure.RATE_LIMITED -> Res.string.discover_groups_like_limit.text(LIKES_A_DAY)
    else -> if (liked) {
        Res.string.discover_groups_like_comment_failed.text()
    } else {
        // "Could not remove your like." names neither a post nor a comment, so it is one key for
        // both surfaces rather than two sentences that happen to agree today.
        Res.string.discover_groups_unlike_failed.text()
    }
}

/** DECISIONS §12: Discover likes are 300 a day, shared between posts and comments. */
private const val LIKES_A_DAY = 300

/**
 * "Today 14:32", "Yesterday 09:05", "Mon 09:05", "03.08.2026 09:05".
 *
 * THE WEEKDAY FORM ARRIVED HERE WITHOUT THIS FUNCTION CHANGING, which is what delegating to
 * `dayLabel` bought. It was still a decision: a byline is a BARE LABEL followed by a clock, joined
 * by `discover_groups_timestamp`'s `%1$s %2$s` with no preposition and nothing to agree with, so
 * the abbreviation «Пн» is grammatical here in both languages. That is the same test the chat
 * list's column and the timeline's separators pass, and it is why all three take `dayLabel`'s
 * default while the sentence-shaped surfaces (both last-seen lines, the mute status line) pass
 * renders the date in its own `Weekday` branch. `TimeFormat.dayLabel` records the split in one place.
 *
 * SHARED WITH `DiscoverPostCard`, which calls this rather than reimplementing it -- a post shown in
 * the feed and the same post shown on its detail screen must not spell their timestamps two ways.
 *
 * SDS §13's post anatomy asks for a timestamp and does not say which kind, so the shape is a
 * DECISION, and the DECISION is to reuse the chat screen's: `dayLabel` plus `formatClockTime`, the
 * two functions the message timeline and the chat list already share. A relative "3h ago" was the
 * alternative and was refused here: nothing ticks on a Discover surface, so it would be a second
 * stale number on a screen that already has one honest one -- `freshnessLabel`, which is relative
 * precisely because the screen re-reads it on a timer.
 *
 * [now] is a parameter rather than a call to the clock, so this stays a pure function and so every
 * timestamp in one list agrees about what "Today" means.
 *
 * THE WORDS COME FROM `dayLabelText`, WHICH IS NOT THIS PACKAGE'S. "Today" and "Yesterday" are the
 * timeline's separators, the header's last-seen line and this byline, and the day one of them was
 * localised and the others were not is the day a Russian phone starts reading half-translated. That
 * function says so at its own definition; this reuses it rather than declaring a second pair of
 * keys with the same English, which would pass every lint and drift on first translation.
 *
 * THE JOIN IS A RESOURCE AND NOT A `"$a $b"`. Both halves arrive as VALUES -- a resolved label and
 * a rendered clock -- and the order and the spacing between them are a language's to choose. The
 * 24-hour clock and `LocalDay.render`'s dd.MM.yyyy are settled in `TimeFormat.kt` and untouched.
 */
internal fun discoverTimestamp(
    at: Instant,
    now: Instant,
    zone: TimeZone = TimeZone.currentSystemDefault(),
): UiText = Res.string.discover_groups_timestamp.text(
    dayLabel(LocalDay.of(at, zone), now, zone).dayLabelText(),
    formatClockTime(at, zone),
)

/**
 * The post's comment count moved by [delta], clamped at zero.
 *
 * A LOCAL ADJUSTMENT, NOT A CLAIM ABOUT THE THREAD. `comment_count` is one server-side number with no
 * per-viewer variant, and this screen learns nothing about comments other people wrote, so after a
 * create or a delete the count means "what the server last said, plus the one thing I just did".
 * `freshnessLabel` over [PostDetailUiState.lastLoadedAt] is what tells the user how old the rest of
 * it is. Re-reading the post after every comment was the alternative, and would double the composer's
 * latency to correct one number by one.
 */
internal fun DiscoverPost.withCommentCountDelta(delta: Int): DiscoverPost =
    copy(commentCount = (commentCount + delta).coerceAtLeast(0))

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

/** Which piece of text the inline editor is open over. Null means the composer has the floor. */
sealed interface DiscoverEdit {
    /** `editDiscoverPost`. [version] is the value the save will be sent with. */
    data class Post(val draft: String, val version: Int) : DiscoverEdit

    /** `editDiscoverComment`. */
    data class Comment(val id: DiscoverCommentId, val draft: String, val version: Int) : DiscoverEdit
}

/** A visual, flat reply: the addressee is represented by the stored @mention, not a nested row. */
data class DiscoverReplyTarget(val username: String, val displayName: String)

data class PostDetailUiState(
    val viewerId: UserId? = null,
    val post: DiscoverPost? = null,
    /** OLDEST FIRST -- the server's own order for a thread. */
    val comments: List<DiscoverComment> = emptyList(),
    /** False until the first read settles, so a blank screen is not called empty before then. */
    val loadedOnce: Boolean = false,
    /**
     * The post cannot be read: deleted, never existed, in a group the caller is not in, or written by
     * somebody they have blocked. The server answers ONE 404 for all four on purpose, so this carries
     * one flag and the screen says one sentence.
     */
    val unavailable: Boolean = false,
    val refreshing: Boolean = false,
    val loadingMore: Boolean = false,
    val hasMore: Boolean = false,
    val nextCursor: String? = null,
    /**
     * When the thread was actually read from the server, not when a read was attempted.
     *
     * Feeds `freshnessLabel`. The same field, with the same name and the same rule, as
     * `DiscoverUiState.lastLoadedAt`: two Discover surfaces with no realtime behind either of them
     * should answer "how old is this" in one voice.
     */
    val lastLoadedAt: Instant? = null,
    /** The composer's RAW text, untrimmed -- the user's cursor lives in it. */
    val draft: String = "",
    val replyingTo: DiscoverReplyTarget? = null,
    val sending: Boolean = false,
    val edit: DiscoverEdit? = null,
    val savingEdit: Boolean = false,
    /** A delete is in flight; the destructive controls disable rather than queueing a second one. */
    val busy: Boolean = false,
    /** A failure about the POST or the THREAD. Shown above the content, never in place of it. */
    val error: UiText? = null,
    /** A failure about ONE action -- a refused like. Same treatment as `DiscoverUiState.notice`. */
    val notice: UiText? = null,
    /**
     * The caller deleted their own post. A ONE-SHOT signal: the screen pops the route and calls
     * [PostDetailViewModel.consumeDismissed], exactly as `GroupUiState.exited` is handled.
     */
    val dismissed: Boolean = false,
    /** True while `createOrGetDirect` is in flight behind the Message button. */
    val opening: Boolean = false,
    /** One-shot, like `PublicProfileUiState.openChatId`. See [PostDetailViewModel.consumeOpened]. */
    val openChatId: ChatId? = null,
    val shareOpen: Boolean = false,
    val shareLoading: Boolean = false,
    val shareChats: List<Chat> = emptyList(),
    val sharingTo: ChatId? = null,
    /**
     * What the post offers this viewer, resolved against the clock at the moment this state was
     * produced.
     *
     * STORED RATHER THAN DERIVED BY THE SCREEN, because deriving it needs two things a composable has
     * no business holding: the signed-in user's id and a clock. Every write goes through one choke
     * point that recomputes this and [commentActions] together -- see `PostDetailViewModel.update`.
     */
    val actions: PostDetailActions = PostDetailActions.NONE,
    /** [actions] per comment, keyed by comment id. Missing means [CommentRowActions.NONE]. */
    val commentActions: Map<String, CommentRowActions> = emptyMap(),
) {
    /** The composer's draft, trimmed and measured. */
    val draftState: DraftState get() = commentDraftState(draft)

    fun actionsFor(comment: DiscoverComment): CommentRowActions =
        commentActions[comment.id.value] ?: CommentRowActions.NONE

    /** Nothing read yet and nothing to paint from: the frame that gets skeletons, not a verdict. */
    val isFirstLoad: Boolean get() = post == null && !loadedOnce
}

// ---------------------------------------------------------------------------------------------
// The view model
// ---------------------------------------------------------------------------------------------

/**
 * One post, its thread, and everything the frozen contract lets a reader do to either.
 *
 * Follows `ui/profile/ProfileViewModel.kt`, `GroupViewModel` and `DiscoverViewModel`: work on
 * `viewModelScope`, state as a `StateFlow`, every `catch` on `Throwable` with `CancellationException`
 * rethrown first, and a failure reported ALONGSIDE the content rather than in place of it.
 */
class PostDetailViewModel(
    private val postId: DiscoverPostId,
    /** Whose screen this is. Every ownership question below is asked against it. */
    private val me: UserId,
    private val source: DiscoverPostDetailSource,
    /**
     * SPS §11.8's Message action. Nullable so a test or a preview can build the screen without the
     * messaging stack; the button is still drawn -- it is one of the things SPS §5.1 names about this
     * surface -- but the tap is a no-op rather than a crash. In the app it is always present.
     */
    private val chats: ChatRepository? = null,
    /** Injected so the 48-hour window and the freshness line are testable without waiting. */
    private val now: () -> Instant = { Clock.System.now() },
    private val realtime: com.swetdot.swetgram.data.realtime.RealtimeClient? = null,
) : ViewModel() {

    private val _state = MutableStateFlow(PostDetailUiState(viewerId = me))
    val state: StateFlow<PostDetailUiState> = _state.asStateFlow()

    private var loading = false

    init {
        refresh()
        realtime?.let { client ->
            viewModelScope.launch {
                client.signals.collect { signal ->
                    if (signal.refreshesPost(postId.value)) refresh()
                }
            }
        }
    }

    /**
     * The ONE place state is written, so the permission flags can never be stale with respect to the
     * content beside them.
     *
     * Recomputing on every write also re-reads the clock, which is what expires an edit affordance on
     * a screen that has been open for two days. Nothing ticks on its own here -- deliberately; see
     * the class header on realtime -- so the next interaction is when it lapses, which is the moment
     * it matters.
     */
    private fun update(block: (PostDetailUiState) -> PostDetailUiState) {
        val next = block(_state.value)
        val at = now()
        _state.value = next.copy(
            actions = next.post?.let { PostDetailActions.of(it, me, at) } ?: PostDetailActions.NONE,
            commentActions = next.comments.associate {
                it.id.value to CommentRowActions.of(it, me, at)
            },
        )
    }

    // ------------------------------------------------------------------------------- reading

    /**
     * `getDiscoverPost` followed by the first page of `listDiscoverComments`.
     *
     * BOTH, ALWAYS. The count on the post and the rows in the thread are two readings of one thing,
     * and refreshing only one of them is how "5 comments" ends up over a list of three.
     *
     * A FAILED REFRESH DOES NOT EMPTY THE SCREEN: the banner appears above whatever is already there,
     * which is `ChatListScreen`'s and `ChatScreen`'s rule and `SwetErrorBanner`'s reason for existing.
     * The one exception is a 404, which is not a failure to show the post -- it is the post not being
     * there, at which point everything else on screen is a control over something that is gone.
     */
    fun refresh() {
        if (loading) return
        loading = true
        update { it.copy(refreshing = true, error = null) }
        viewModelScope.launch {
            try {
                val post = source.post(postId)
                val page = source.comments(postId, cursor = null)
                update {
                    it.copy(
                        post = post,
                        comments = page.items,
                        hasMore = page.hasMore,
                        nextCursor = page.nextCursor,
                        loadedOnce = true,
                        unavailable = false,
                        refreshing = false,
                        // Moved only on a read that SUCCEEDED, because it is what the freshness line
                        // is a statement about.
                        lastLoadedAt = now(),
                        error = null,
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                val gone = error.asDiscoverFailure() == DiscoverFailure.UNAVAILABLE
                update {
                    it.copy(
                        loadedOnce = true,
                        refreshing = false,
                        unavailable = gone,
                        post = if (gone) null else it.post,
                        comments = if (gone) emptyList() else it.comments,
                        hasMore = if (gone) false else it.hasMore,
                        edit = if (gone) null else it.edit,
                        // The empty state says the sentence, so a banner repeating it would be the
                        // screen apologising twice for one absence.
                        error = if (gone) null else error.discoverBanner(Res.string.discover_groups_post_load_failed.text()),
                    )
                }
            } finally {
                loading = false
            }
        }
    }

    /**
     * The next page of the thread, appended at the END.
     *
     * The thread is oldest-first, so paging walks FORWARD in time and a page belongs after what is
     * already shown. Getting that backwards is what makes a thread read in reverse.
     */
    fun loadMore() {
        val current = _state.value
        val cursor = current.nextCursor ?: return
        if (current.loadingMore || !current.hasMore) return

        update { it.copy(loadingMore = true) }
        viewModelScope.launch {
            try {
                val page = source.comments(postId, cursor = cursor)
                update { state ->
                    val seen = state.comments.mapTo(mutableSetOf()) { it.id.value }
                    state.copy(
                        // De-duplicated on the id: a comment deleted between two pages shifts the
                        // keyset, and a repeated row would give the LazyColumn two items with one
                        // key -- which is a crash, not a cosmetic problem.
                        comments = state.comments + page.items.filter { it.id.value !in seen },
                        hasMore = page.hasMore,
                        nextCursor = page.nextCursor,
                        loadingMore = false,
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                update {
                    it.copy(
                        loadingMore = false,
                        // hasMore is left alone: the page failed, it did not turn out to be the end.
                        error = error.discoverBanner(Res.string.discover_groups_comments_more_failed.text()),
                    )
                }
            }
        }
    }

    // ----------------------------------------------------------------------------- composing

    fun onDraftChange(text: String) {
        update { state ->
            val target = state.replyingTo
            state.copy(
                draft = text,
                replyingTo = target?.takeIf { text.contains("@${it.username}", ignoreCase = true) },
            )
        }
    }

    fun beginReply(comment: DiscoverComment) {
        val username = comment.author.username.lowercase()
        val target = DiscoverReplyTarget(username, comment.author.displayName)
        update { state ->
            val previousPrefix = state.replyingTo?.let { "@${it.username} " }
            val body = previousPrefix
                ?.takeIf { state.draft.startsWith(it, ignoreCase = true) }
                ?.let { state.draft.drop(it.length) }
                ?: state.draft
            state.copy(
                draft = "@$username ${body.trimStart()}",
                replyingTo = target,
                edit = null,
            )
        }
    }

    fun cancelReply() {
        update { state ->
            val target = state.replyingTo ?: return@update state
            val prefix = "@${target.username} "
            state.copy(
                draft = if (state.draft.startsWith(prefix, ignoreCase = true)) {
                    state.draft.drop(prefix.length)
                } else {
                    state.draft
                },
                replyingTo = null,
            )
        }
    }

    /**
     * `createDiscoverComment`.
     *
     * NEVER RETRIED AUTOMATICALLY, and this is where that has teeth. The operation carries no
     * `client_id` and declares no `Idempotency-Key`, so the server has nothing to deduplicate against
     * and a retry on a dropped response posts the comment TWICE. The draft is kept on failure and the
     * person decides.
     *
     * The new comment is appended locally rather than triggering a reload: in an oldest-first thread
     * it is the last row by construction, and a reload here would scroll the user away from the thing
     * they just wrote.
     */
    fun sendComment() {
        val current = _state.value
        if (current.sending || current.unavailable) return
        val draft = current.draftState
        if (!draft.canSend) return

        update { it.copy(sending = true, error = null) }
        viewModelScope.launch {
            try {
                val comment = source.addComment(postId, draft.text)
                update {
                    it.copy(
                        comments = it.comments + comment,
                        post = it.post?.withCommentCountDelta(1),
                        // Cleared only on success. A cleared draft over a failed send is somebody's
                        // words gone, with an apology in their place.
                        draft = "",
                        replyingTo = null,
                        sending = false,
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                update {
                    it.copy(
                        sending = false,
                        error = error.discoverBanner(Res.string.discover_groups_comment_post_failed.text()),
                    )
                }
            }
        }
    }

    // --------------------------------------------------------------------------------- edits

    /** Opens the inline editor over the post. Refused once the window has closed. */
    fun beginEditPost() {
        val current = _state.value
        val post = current.post ?: return
        if (!current.actions.canEdit) return
        update {
            it.copy(
                edit = DiscoverEdit.Post(draft = post.content, version = post.version),
                error = null,
            )
        }
    }

    fun beginEditComment(comment: DiscoverComment) {
        if (!_state.value.actionsFor(comment).canEdit) return
        update {
            it.copy(
                edit = DiscoverEdit.Comment(
                    id = comment.id,
                    draft = comment.content,
                    version = comment.version,
                ),
                error = null,
            )
        }
    }

    fun onEditDraftChange(text: String) {
        update { state ->
            state.copy(
                edit = when (val edit = state.edit) {
                    null -> null
                    is DiscoverEdit.Post -> edit.copy(draft = text)
                    is DiscoverEdit.Comment -> edit.copy(draft = text)
                },
            )
        }
    }

    fun cancelEdit() {
        if (_state.value.savingEdit) return
        update { it.copy(edit = null) }
    }

    /**
     * `editDiscoverPost` or `editDiscoverComment`, whichever the editor is open over.
     *
     * THE VERSION SENT IS THE ONE THAT WAS READ, carried on [DiscoverEdit] since the editor opened. A
     * stale value is `409 version_conflict` -- verified live on 2026-08-06 -- which the repository
     * turns into a `Stale` VALUE rather than an exception, so this `when` cannot forget it.
     * Re-reading the version at save time would defeat the mechanism entirely: it would make every
     * concurrent edit a last-writer-wins overwrite of somebody's work.
     *
     * THE TWO STALE BRANCHES DIFFER BECAUSE THE CONTRACT DOES. `DiscoverPostWrite.Stale` carries the
     * post the server holds now, so the screen can show the current text under a banner in one step.
     * `DiscoverCommentWrite.Stale` carries nothing -- there is no `getDiscoverComment` anywhere in
     * v1.0.0 -- so the only honest recovery is to re-list the thread, which is what this does.
     *
     * THE EDITOR STAYS OPEN IN BOTH CASES, with the user's text still in it. Somebody who has just
     * lost a race needs their words to copy out of, not a closed box and an apology.
     */
    fun saveEdit() {
        val current = _state.value
        val edit = current.edit ?: return
        if (current.savingEdit) return

        val draft = when (edit) {
            is DiscoverEdit.Post -> postDraftState(edit.draft)
            is DiscoverEdit.Comment -> commentDraftState(edit.draft)
        }
        if (!draft.canSend) return

        update { it.copy(savingEdit = true, error = null) }
        viewModelScope.launch {
            try {
                when (edit) {
                    is DiscoverEdit.Post -> when (val write = source.editPost(postId, draft.text, edit.version)) {
                        is DiscoverPostWrite.Updated ->
                            update { it.copy(post = write.post, edit = null, savingEdit = false) }
                        is DiscoverPostWrite.Stale -> update {
                            it.copy(
                                post = write.current,
                                // Re-armed with the version the server actually holds, so the next
                                // Save can succeed instead of losing the same race forever.
                                edit = DiscoverEdit.Post(edit.draft, write.current.version),
                                savingEdit = false,
                                error = STALE_POST_MESSAGE,
                            )
                        }
                    }
                    is DiscoverEdit.Comment -> when (val write = source.editComment(edit.id, draft.text, edit.version)) {
                        is DiscoverCommentWrite.Updated -> update { state ->
                            state.copy(
                                comments = state.comments.map {
                                    if (it.id == write.comment.id) write.comment else it
                                },
                                edit = null,
                                savingEdit = false,
                            )
                        }
                        DiscoverCommentWrite.Stale -> {
                            update { it.copy(savingEdit = false, error = STALE_COMMENT_MESSAGE) }
                            // The only read of a comment is the paged listing, so the thread has to
                            // come back whole. The editor is left open over the user's own text.
                            refresh()
                        }
                    }
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                update {
                    it.copy(
                        savingEdit = false,
                        error = error.discoverBanner(Res.string.discover_groups_save_failed.text()),
                    )
                }
            }
        }
    }

    // ------------------------------------------------------------------------------- deletes

    /**
     * `deleteDiscoverPost`. Permanent, and it takes the whole thread with it.
     *
     * The confirmation lives on the screen -- SDS §11.1, "Use confirmation dialogs for destructive
     * actions" -- and the permission is re-checked here because the state this screen was drawn from
     * can be a minute old.
     *
     * A POST THAT IS ALREADY GONE COUNTS AS SUCCESS, exactly as `GroupViewModel.deleteGroup` treats a
     * group that is already gone: it is what was asked for, and reporting a failure over something
     * that no longer exists leaves the user tapping a button whose work is done. A second
     * `deleteDiscoverPost` answers 404 rather than 204 -- observed live -- so this branch is real.
     */
    fun deletePost() {
        val current = _state.value
        if (current.busy || !current.actions.canDelete) return

        update { it.copy(busy = true, error = null) }
        viewModelScope.launch {
            try {
                source.deletePost(postId)
                update { it.copy(busy = false, dismissed = true) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                if (error.asDiscoverFailure() == DiscoverFailure.UNAVAILABLE) {
                    update { it.copy(busy = false, dismissed = true) }
                } else {
                    update {
                        it.copy(
                            busy = false,
                            error = error.discoverBanner(Res.string.discover_groups_delete_post_failed.text()),
                        )
                    }
                }
            }
        }
    }

    /**
     * `deleteDiscoverComment`. 204 whatever happens, which is why the guard in front of it matters
     * more than the response behind it -- see [CommentRowActions.canDelete].
     *
     * THE ROW IS DROPPED LOCALLY RATHER THAN RE-LISTED. `DiscoverRepository.deleteComment` warns that
     * "a 204 here is NOT evidence that anything was removed. A screen should re-list rather than trust
     * it", and that warning is right for a caller that could aim this at a comment it does not own.
     * This one cannot: the control is drawn only when `DiscoverComment.mayDelete` is true, and the
     * server removes exactly the rows matching `(id, author_id)`. Re-listing would throw away the
     * user's scroll position to re-confirm something already known, on the one action where an
     * immediate response matters most. The freshness line still says how old the rest of the thread
     * is.
     */
    fun deleteComment(comment: DiscoverComment) {
        val current = _state.value
        if (current.busy || !current.actionsFor(comment).canDelete) return

        update { it.copy(busy = true, error = null) }
        viewModelScope.launch {
            try {
                source.deleteComment(comment.id)
                update { state ->
                    state.copy(
                        comments = state.comments.filterNot { it.id == comment.id },
                        post = state.post?.withCommentCountDelta(-1),
                        // The editor cannot outlive the comment it was open over.
                        edit = state.edit.takeUnless {
                            it is DiscoverEdit.Comment && it.id == comment.id
                        },
                        busy = false,
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                update {
                    it.copy(
                        busy = false,
                        error = error.discoverBanner(Res.string.discover_groups_delete_comment_failed.text()),
                    )
                }
            }
        }
    }

    // --------------------------------------------------------------------------------- likes

    /**
     * `likeDiscoverPost` / `unlikeDiscoverPost`, as the STATE to reach rather than a flip.
     *
     * OPTIMISTIC, THEN RECONCILED. `DiscoverPost.withLike` is applied on the tap -- SDS §19.4 names
     * "Like action" among the things motion is for, which only means anything if the heart moves
     * before the round trip -- and the repository's four outcomes are then honoured exactly:
     *
     *  * `Settled` replaces the post with what the server holds, because `like_count` is not this
     *    viewer's to predict: other people are liking the same post at the same time.
     *  * `Accepted` keeps the optimistic value. The write LANDED and only the confirming re-read did
     *    not; putting the heart back would deny a like the server is holding.
     *  * `Failed` reverts, with a notice rather than a banner -- this is one row's problem, not the
     *    thread's.
     *  * `Superseded` does NOTHING AT ALL. A later tap owns the state on screen, and reverting here
     *    would undo it.
     *
     * NOT GUARDED AGAINST A SECOND TAP. The repository serialises per item and reports `Superseded`
     * for the loser, so rapid taps resolve to the last intent; disabling the heart while a write is in
     * flight would make the control feel stuck for a whole round trip and would throw away that
     * design.
     *
     * [target] IS THE STATE TO REACH, not a flip, all the way from the tap: `DiscoverLikeButton`'s
     * caller computes `!likedByMe` once, from the post it is drawing, and it travels unchanged into
     * `POST` or `DELETE`. Nothing between here and the socket re-reads the current value, so nothing
     * can invert it.
     */
    fun setPostLiked(target: Boolean) {
        if (_state.value.post == null) return
        update { it.copy(post = it.post?.withLike(target), notice = null) }

        viewModelScope.launch {
            try {
                when (val result = source.setPostLiked(postId, target)) {
                    is DiscoverLikeResult.Settled -> update { it.copy(post = result.post) }
                    DiscoverLikeResult.Accepted -> Unit
                    DiscoverLikeResult.Superseded -> Unit
                    is DiscoverLikeResult.Failed -> update {
                        it.copy(
                            post = it.post?.withLike(!target),
                            notice = likeNotice(result.failure, target),
                        )
                    }
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                update {
                    it.copy(
                        post = it.post?.withLike(!target),
                        notice = likeNotice(error.asDiscoverFailure(), target),
                    )
                }
            }
        }
    }

    fun setPostSaved(target: Boolean) {
        val current = _state.value
        if (current.busy || current.post == null) return
        update { it.copy(post = it.post?.withSaved(target), busy = true, notice = null) }
        viewModelScope.launch {
            try {
                val settled = source.setPostSaved(postId, target)
                update { it.copy(post = settled, busy = false) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                update {
                    it.copy(
                        post = it.post?.withSaved(!target),
                        busy = false,
                        notice = Res.string.discover_groups_bookmark_failed.text(),
                    )
                }
            }
        }
    }

    fun openSharePicker() {
        val repository = chats ?: return
        val current = _state.value
        if (current.shareLoading || current.sharingTo != null) return
        update { it.copy(shareOpen = true, shareLoading = true, notice = null) }
        viewModelScope.launch {
            try {
                runCatching { repository.refresh(archived = false) }
                val available = repository.observeChats(archived = false).first()
                update { it.copy(shareChats = available, shareLoading = false) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                update {
                    it.copy(
                        shareOpen = false,
                        shareLoading = false,
                        notice = Res.string.discover_groups_share_failed.text(),
                    )
                }
            }
        }
    }

    fun dismissSharePicker() {
        if (_state.value.sharingTo != null) return
        update { it.copy(shareOpen = false, shareLoading = false) }
    }

    fun shareTo(chatId: ChatId) {
        val current = _state.value
        if (!current.shareOpen || current.shareLoading || current.sharingTo != null) return
        update { it.copy(sharingTo = chatId) }
        viewModelScope.launch {
            try {
                source.sharePost(postId, chatId)
                update {
                    it.copy(
                        shareOpen = false,
                        sharingTo = null,
                        notice = Res.string.discover_groups_share_sent.text(),
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                update {
                    it.copy(
                        sharingTo = null,
                        notice = Res.string.discover_groups_share_failed.text(),
                    )
                }
            }
        }
    }

    fun setPlanJoined(joined: Boolean) {
        val current = _state.value
        if (current.busy || current.post?.plan == null) return
        update { it.copy(busy = true, notice = null) }
        viewModelScope.launch {
            try {
                val post = if (joined) source.joinPlan(postId) else source.leavePlan(postId)
                update { it.copy(post = post, busy = false) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                update {
                    it.copy(
                        busy = false,
                        notice = Res.string.discover_groups_plan_action_failed.text(),
                    )
                }
            }
        }
    }

    fun updatePlanStatus(status: DiscoverPostStatus) {
        val current = _state.value
        val post = current.post ?: return
        if (current.busy || post.plan == null ||
            (status != DiscoverPostStatus.COMPLETED && status != DiscoverPostStatus.CANCELLED)
        ) return
        update { it.copy(busy = true, notice = null) }
        viewModelScope.launch {
            try {
                val updated = source.updatePlanStatus(postId, status, post.version)
                update { it.copy(post = updated, busy = false) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                update {
                    it.copy(
                        busy = false,
                        notice = Res.string.discover_groups_plan_action_failed.text(),
                    )
                }
            }
        }
    }

    fun acceptAnswer(comment: DiscoverComment) {
        val current = _state.value
        val post = current.post ?: return
        if (current.busy || !post.isMine(me) || post.type != com.swetdot.swetgram.domain.DiscoverPostType.QUESTION) return
        update { it.copy(busy = true, notice = null) }
        viewModelScope.launch {
            try {
                val updated = source.acceptAnswer(postId, comment.id, post.version)
                update {
                    it.copy(
                        post = updated,
                        comments = it.comments.map { row -> row.copy(isAccepted = row.id == comment.id) },
                        busy = false,
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                update { it.copy(busy = false, notice = Res.string.discover_groups_answer_action_failed.text()) }
            }
        }
    }

    fun clearAcceptedAnswer() {
        val current = _state.value
        val post = current.post ?: return
        if (current.busy || !post.isMine(me) || post.acceptedAnswer == null) return
        update { it.copy(busy = true, notice = null) }
        viewModelScope.launch {
            try {
                val updated = source.clearAcceptedAnswer(postId, post.version)
                update {
                    it.copy(
                        post = updated,
                        comments = it.comments.map { row -> row.copy(isAccepted = false) },
                        busy = false,
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                update { it.copy(busy = false, notice = Res.string.discover_groups_answer_action_failed.text()) }
            }
        }
    }

    /**
     * [setPostLiked] for one comment. Always available -- the server gates likes on nothing.
     *
     * `Settled` IS UNREACHABLE HERE and is handled anyway. `DiscoverRepository.setCommentLiked` can
     * only answer `Accepted`, because there is no `getDiscoverComment` to reconcile against, so the
     * optimistic count stands until the thread is next listed. The branch exists so the `when` is
     * exhaustive over the type rather than over today's implementation of it.
     */
    fun setCommentLiked(comment: DiscoverComment, target: Boolean) {
        fun applyTo(state: PostDetailUiState, liked: Boolean) = state.copy(
            comments = state.comments.map { if (it.id == comment.id) it.withLike(liked) else it },
        )

        update { applyTo(it, target).copy(notice = null) }

        viewModelScope.launch {
            try {
                when (val result = source.setCommentLiked(comment.id, target)) {
                    is DiscoverLikeResult.Settled, DiscoverLikeResult.Accepted -> Unit
                    DiscoverLikeResult.Superseded -> Unit
                    is DiscoverLikeResult.Failed -> update {
                        applyTo(it, !target).copy(
                            notice = commentLikeNotice(result.failure, target),
                        )
                    }
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                update {
                    applyTo(it, !target).copy(
                        notice = commentLikeNotice(error.asDiscoverFailure(), target),
                    )
                }
            }
        }
    }

    // ------------------------------------------------------------------------ message author

    /**
     * SPS §5.1's "Message author from Discover post", §11.8's flow: tap Message, open or create the
     * private chat with the author.
     *
     * `createOrGetDirect` is idempotent BY STATUS -- 201 when it created the conversation, 200 when
     * one already existed -- so a second tap cannot produce a second thread, and
     * [PostDetailUiState.opening] exists for the user rather than for the server.
     *
     * THE REFUSAL IS NOT DECODED, AND THAT IS THE WHOLE POINT. A recipient who has blocked the caller,
     * a caller who has blocked the recipient, and a recipient whose `allow_messages_from` excludes the
     * caller all come back as the SAME `403 recipient_unavailable`. `ConversationService` chooses that
     * deliberately and `ERRORS.md` says why: "The API deliberately uses `recipient_unavailable`
     * instead of revealing that another user applied a block." So every `Forbidden` gets ONE neutral
     * sentence, and the code is not even read. Guessing between the cases would rebuild the block
     * detector the server refuses to be -- and would be wrong outright for the privacy-setting case,
     * which is not a block at all. `PublicProfileViewModel.openChat` takes the same shape.
     */
    fun messageAuthor() {
        val repository = chats ?: return
        val current = _state.value
        val post = current.post ?: return
        if (current.opening || !current.actions.canMessageAuthor) return

        update { it.copy(opening = true, error = null) }
        viewModelScope.launch {
            try {
                val chatId = repository.openDirectChat(post.author.id.value)
                update { it.copy(opening = false, openChatId = chatId) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                update {
                    it.copy(
                        // ONE FUNCTION FOR BOTH DISCOVER SURFACES, which is what it was already
                        // supposed to be: this was the same three-line `when` written out again, and
                        // writing it twice is how the two came to disagree. `messageAuthorNotice`
                        // carries the fix neither copy had -- `403 email_not_verified` comes from
                        // the CALLER's own unverified email (`createOrGetDirect` checks it before it
                        // looks at the recipient at all), so answering it with "You cannot start a
                        // chat with this account." blamed the author for something only the reader
                        // can put right.
                        error = messageAuthorNotice(error),
                    )
                }
            }
        }
    }

    // ------------------------------------------------------------------------------ one-shots

    /** Consumed by the caller BEFORE it navigates; see [PostDetailUiState.openChatId]. */
    fun consumeOpened() {
        update { it.copy(openChatId = null) }
    }

    /** Consumed by the caller BEFORE it pops; see [PostDetailUiState.dismissed]. */
    fun consumeDismissed() {
        update { it.copy(dismissed = false) }
    }

    fun dismissError() {
        update { it.copy(error = null) }
    }

    fun dismissNotice() {
        update { it.copy(notice = null) }
    }
}

/** A post edit that lost a race. The current text is already on screen; the version is re-armed. */
internal val STALE_POST_MESSAGE: UiText = Res.string.discover_groups_stale_post.text()

/** A comment edit that lost a race. The thread is being re-listed; there is no single-comment read. */
internal val STALE_COMMENT_MESSAGE: UiText = Res.string.discover_groups_stale_comment.text()

/**
 * The one sentence said about a refused `createOrGetDirect`.
 *
 * States the outcome and stops. "They may have blocked you" and "check their privacy settings" would
 * each resolve an ambiguity the API went out of its way to create, and each would be wrong half the
 * time.
 */
internal val RECIPIENT_UNAVAILABLE_MESSAGE: UiText =
    Res.string.discover_groups_recipient_unavailable.text()
